<script setup lang="ts">
import { computed, reactive } from 'vue'
import { NButton, NInput, useMessage } from 'naive-ui'
import { inputStatus } from '../composables/inputStatus'
import { useAgentReposStore } from '../stores/agent_repos'

// Write-only set/rotate field for one of a repo's stored credentials. The
// token is only ever sent, never shown: the page renders `isSet` instead.
const props = defineProps<{
  slug: string
  param: 'github_token' | 'registry_token'
  label: string
  isSet: boolean
  placeholder: string
  testidPrefix: string
  hint?: string
}>()

const store = useAgentReposStore()
const sentenceLabel = computed(() => props.label.charAt(0).toUpperCase() + props.label.slice(1))
const message = useMessage()

// `missing` keeps the input in error state after the validation toast has
// auto-dismissed, and clears itself once the field has content.
const draft = reactive({ token: '', saving: false, missing: false })

async function save(): Promise<void> {
  const token = draft.token.trim()
  if (!token) {
    draft.missing = true
    message.error('Enter a token to save')
    return
  }
  draft.saving = true
  try {
    await store.update(props.slug, { [props.param]: token })
    draft.token = ''
    message.success(`${sentenceLabel.value} updated`)
  } catch (e) {
    message.error(e instanceof Error ? e.message : `Failed to update ${props.label}`)
  } finally {
    draft.saving = false
  }
}
</script>

<template>
  <label class="flex flex-col gap-1 text-xs text-white/60">
    {{ isSet ? `Rotate ${label}` : `Set ${label}` }}
    <span v-if="hint" class="text-white/40" :data-testid="`${testidPrefix}-hint-${slug}`">{{ hint }}</span>
    <div class="flex gap-2">
      <NInput
        v-model:value="draft.token"
        type="password"
        show-password-on="click"
        :placeholder="isSet ? 'Enter a new token to rotate' : placeholder"
        :disabled="draft.saving"
        :status="inputStatus(draft.missing, draft.token)"
        class="flex-1"
        :data-testid="`${testidPrefix}-input-${slug}`"
      />
      <NButton
        size="small"
        type="primary"
        :loading="draft.saving"
        :data-testid="`${testidPrefix}-save-${slug}`"
        @click="save"
        >Save token</NButton
      >
    </div>
  </label>
</template>
